import { Fragment, h, render } from "preact";
import { useEffect, useState, useRef, useCallback } from "preact/hooks";
import { ArrowLeft, ArrowRight, Plus, CloseIcon } from "../icons.jsx";
import storefront from "../storefront";
import * as queries from "../storefront/queries";
import ProductCardPrice from "./product-card-price.jsx";
import SwatchImage from "../../main-product/swatchImage.jsx";
import getSellingPlanId from "../getSellingPlanId.js";
import { isColor, findColorOptionName } from "../colors.js";

const splitImages = (product) => {
  const images = {
    variant: [],
    nonVariant: [],
  };

  const variantsImages = product?.variants.edges
    .map((edge) => edge?.node?.image)
    .filter((image) => image);

  product?.images.edges.forEach(({ node }) => {
    const variantImage = variantsImages.find((v) => {
      return v.src === node.src;
    });

    if (variantImage) {
      images.variant.push(variantImage);
    } else {
      images.nonVariant.push(node);
    }
  });

  return images;
};

const ProductCard = ({
  data,
  gallery,
  cardStyle,
  index,
  preselectColor,
  expandColors,
}) => {
  // Product Data
  const product = data;
  const nonOpenBoxProduct = product.nonOpenBoxProduct?.reference;

  const variants = product.variants.edges;

  const variantsImages = variants
    .map((edge) => edge?.node?.image)
    .filter((image) => image);

  let productImages = product?.images?.edges.map((edge) => edge.node);

  if (nonOpenBoxProduct) {
    const productVariantImages = splitImages(product);
    const nonOpenBoxProductVariantImages = splitImages(nonOpenBoxProduct);

    productImages = [
      ...nonOpenBoxProductVariantImages.nonVariant,
      ...productVariantImages.variant,
    ];
  }

  const images = productImages.filter((i) => {
    return !variantsImages.find((v) => {
      return v.src === i.src;
    });
  });

  const group = product.tags?.find((t) => t.includes("group:"));
  const groupStyle = product.tags
    ?.find((t) => t.includes("group:"))
    ?.split(":")[2];
  const b = product.tags?.find((t) => t.includes("badge:"))?.split(":");
  const badgeText = b ? b[b?.length - 1] : null;
  const badgeColor = b?.length > 2 ? b[1] : "";

  const colorOptionName = findColorOptionName(product.options);

  // Product Options
  const colors = product.options
    .filter((o) => {
      return isColor(o.name);
    })[0]
    ?.values.toSorted((a, b) => {
      if (a === preselectColor) return -1;
      if (b === preselectColor) return 1;
      return 0;
    });

  const options = product?.options
    ?.filter(
      (option) =>
        !isColor(option.name) &&
        option.name != "Default Title" &&
        option.name != "Title",
    )
    .map((option) => {
      // Remove custom gift card option (only supported on pdp)
      if (option.name !== "Amount") return option;

      option.values = option.values.filter(
        (value) => value !== "Custom Value ($)",
      );
      return option;
    });

  const findPreselectedVariant = () => {
    if (preselectColor) {
      const matchingVariant = variants.find(({ node }) => {
        const colorOption = node.selectedOptions.find((option) =>
          isColor(option.name),
        );

        return colorOption && colorOption.value === preselectColor;
      });

      if (matchingVariant) {
        return matchingVariant.node;
      }
    }

    return variants[0].node;
  };

  const initialVariant = findPreselectedVariant();
  // Set Initial Variant Options
  let variantOptions = product?.options.map((edge) => ({
    [edge?.name]: initialVariant?.selectedOptions.find(
      (element) => element.name == [edge?.name],
    ).value,
  }));

  // Set Pricing Options
  const priceMax = parseFloat(product?.priceRange?.maxVariantPrice?.amount);
  const priceMin = parseFloat(product?.priceRange?.minVariantPrice?.amount);

  const swiperParams = {
    slidesPerView: "auto",
    loop: true,
    rebuildOnUpdate: true,
  };

  let swatchCount = null;
  try {
    swatchCount = colors?.length > 3 ? colors?.length - 3 : null;
    swatchCount = colors?.length || null;
  } catch (error) {
    console.log("No Colors available");
  }

  const [initial, setInitial] = useState(true);

  const [variant, setVariant] = useState(initialVariant);
  const [productGroup, setProductGroup] = useState(null);
  const [productGroupOptions, setProductGroupOptions] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState(variantOptions);
  const [variantImages, setVariantImages] = useState(images);
  const [addTray, setAddTray] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const quickAddRef = useRef(null);
  const quickAddContainerRef = useRef(null);
  const [initialImages, setInitialImages] = useState(
    preselectColor
      ? initialVariant.image
      : (nonOpenBoxProduct || product).coverImage?.reference.image ||
          initialVariant.image,
  );

  const swiper = useRef(null);

  const productUrl = `${Shopify.routes.root}products/${data?.handle}${preselectColor ? `?variant=${variant.id.replace("gid://shopify/ProductVariant/", "")}` : ""}`;

  const getProductGroup = async () => {
    if (!group) return;
    const response = await storefront.request({
      query: queries.productsByTag(group),
    });
    if (response?.data?.products) {
      const products = response.data.products.edges.filter((p) => p);
      let productsClean = products.map((p) => p.node?.variants?.edges[0]),
        productOptions = productsClean.map((p) => p.node?.selectedOptions[0]);
      if (!productsClean) return;
      setProductGroupOptions(productOptions);
      setProductGroup(productsClean);
    }
  };

  const addBundleToCart = async () => {
    let nodes = JSON.parse(product.bundleProducts?.value),
      v;
    const ids = nodes.map((item) => parseInt(item.split("/").pop()));
    if (!ids) return;
    const response = await storefront.request({
      query: queries.products,
      variables: {
        ids: ids.map((id) => encodeId(id, "Product")),
      },
    });

    if (response) {
      v = response?.data?.nodes;
    }
    if (!v) return;
    let bundlePrice = product.variants?.edges[0].node?.priceV2?.amount * 100,
      originalPrice =
        product.variants?.edges[0].node?.compareAtPriceV2?.amount * 100;
    const bundleId = ids.map((id) => id).join("/");
    const { title, featured_image } = product;
    const qty = 1;
    const titles = v.map(
      (vp) =>
        `${vp.title}${
          vp?.variants?.edges[0]?.node?.title != "Default Title"
            ? `: ${vp?.variants?.edges[0]?.node?.title}`
            : ""
        }`,
    );
    const items = v.map((vo) => {
      let vp = vo?.variants?.edges[0].node;
      if (!vp) return;
      const inventoryReno = vp.inventoryReno?.value
        ? parseInt(vp.inventoryReno.value)
        : null;
      const inventoryAtlanta = vp.inventoryAtlanta?.value
        ? parseInt(vp.inventoryAtlanta.value)
        : null;

      return {
        id: vp.id.split("/").pop(),
        qty,
        properties: {
          _inventoryReno: inventoryReno,
          _inventoryAtlanta: inventoryAtlanta,
          _bundle: product.handle,
          _bundleId: bundleId,
          _bundlePrice: bundlePrice,
          _originalPrice: originalPrice,
          _bundleTitles: titles.join("+"),
          _bundleTitle: title,
          _bundleImage: featured_image,
          _bundleQuantity: qty,
          _selling_plan: getSellingPlanId(vp),
          _added_by: "Product Card addBundleToCart",
        },
        selling_plan: getSellingPlanId(vp),
      };
    });
    window.addToCart(items);
  };

  const addItemToCart = (id) => {
    const inventoryReno = variant.inventoryReno?.value
      ? parseInt(variant.inventoryReno.value)
      : null;
    const inventoryAtlanta = variant.inventoryAtlanta?.value
      ? parseInt(variant.inventoryAtlanta.value)
      : null;

    const properties = {
      _inventoryReno: inventoryReno,
      _inventoryAtlanta: inventoryAtlanta,
      _selling_plan: getSellingPlanId(variant),
      _added_by: "Product Card addItemToCart",
    };

    window.addToCart(
      [
        {
          id,
          quantity: 1,
          properties,
          selling_plan: getSellingPlanId(variant),
        },
      ],
      {
        onError: (response) => console.error(response),
        onBeforeAdd: () => setAddingToCart(true),
        onAfterAdd: () =>
          setTimeout(() => {
            setAddingToCart(false);
          }, 200),
      },
    );
  };

  const handleVariantChange = (option, val) => {
    const selectedOption = selectedOptions.map((o) => {
      if (Object.keys(o) == option) {
        return {
          ...o,
          [option]: val,
        };
      }
      return o;
    });
    const currentOption = {
      [option]: val,
    };

    let vset =
      productGroup && option.toLowerCase() == groupStyle
        ? productGroup
        : variants;
    let selectedVariant = vset.filter((v) => {
      let vo = product?.options.map((edge) => ({
        [edge?.name]: v?.node?.selectedOptions.find(
          (element) => element.name == [edge?.name],
        ).value,
      }));
      return JSON.stringify(vo) === JSON.stringify(selectedOption);
    });

    if (!selectedVariant[0]?.node) {
      selectedVariant = vset.filter((v) => {
        let set = v?.node?.selectedOptions.find(
          (element) => element.name == [option] && element.value == [val],
        );
        return set?.value === currentOption[option];
      });
    }

    let sv = selectedVariant.length > 0 ? selectedVariant[0] : selectedVariant,
      svo = sv.node.selectedOptions.map((option) => ({
        [option.name]: option.value,
      }));

    setSelectedOptions(svo);
    return setVariant(sv.node);
  };

  const handleQuickAdd = (e) => {
    e.preventDefault();
    if (!product.tags.includes("bundle")) {
      addItemToCart(variant.id.split("/").pop());
    } else {
      addBundleToCart();
    }
  };

  const handleVariantActive = (name, val) => {
    let vo = Object.assign({}, ...selectedOptions);
    return vo[name] == val ? "--active" : "";
  };

  const getImagePadding = (item) => {
    const fullBleed = item?.altText?.includes("#full-bleed");
    const multiply = item?.altText?.includes("#multiply");
    return `${fullBleed ? "full" : ""} ${multiply ? "multiply" : ""}`;
  };

  useEffect(() => {
    getProductGroup();
  }, [product]);

  useEffect(() => {
    let vi = variant?.image?.src;
    vi = vi && productImages.filter((i) => vi.includes(i.src));
    vi = vi && vi.length > 0 ? vi : productImages;
    let vis = images.filter((val) => !vi.includes(val));
    vis =
      vis.length > 0
        ? vi.concat(vis)
        : vi
          ? vi.concat(productImages)
          : productImages;
    if (!initial) {
      setInitialImages(vis[0]);
      swiper?.current?.swiper.slideToLoop(0, 0);
    }
    setInitial(false);
    return setVariantImages(vis);
  }, [variant]);

  const closeTray = useCallback(
    (focus) => {
      if (!addTray) return;
      setAddTray(false);
      if (focus && quickAddRef?.current) {
        quickAddRef.current.focus();
      }
    },
    [addTray, setAddTray, quickAddRef],
  );

  useEffect(() => {
    if (!quickAddContainerRef.current) return;

    const handleFocusOut = (e) => {
      if (!quickAddContainerRef.current) return;

      if (
        e.relatedTarget &&
        quickAddContainerRef.current.contains(e.relatedTarget)
      )
        return;

      closeTray(false);
    };

    quickAddContainerRef.current.addEventListener("focusout", handleFocusOut);

    return () =>
      quickAddContainerRef.current.removeEventListener(
        "focusout",
        handleFocusOut,
      );
  }, [closeTray, quickAddContainerRef]);

  useEffect(() => {
    const closeEsc = (e) => {
      if (e.key === "Escape") {
        closeTray(true);
      }
    };

    document.addEventListener("keydown", closeEsc);

    return () => document.removeEventListener("keydown", closeEsc);
  }, [addTray, setAddTray, closeTray]);

  const title = nonOpenBoxProduct
    ? `${nonOpenBoxProduct.title} (Open Box)`
    : product.title;

  const productTitle = expandColors ? `${title} - ${preselectColor}` : title;

  return (
    <div
      className={`product-card product-card--fade-in ${cardStyle || ""}`}
      // ref={(el) => (cardFade = el)}
      data-product-card
      data-product-id={`${product.id.split("/").pop()}`}
      style={{
        "--index": Math.min(index, 8),
      }}
    >
      {/* Creating a temp url till the password is lifted */}
      {/* Will replace with product.onlineStoreUrl */}
      <div className="product-card__images">
        {/* Product Card Badging */}
        {!variant?.availableForSale ? (
          product.oosMessage?.value ? (
            <p className={`product-card__badge`}>{product.oosMessage?.value}</p>
          ) : badgeText ? (
            <p className={`product-card__badge ${badgeColor} ${badgeText}`}>
              {badgeText}
            </p>
          ) : null
        ) : variant.compareAtPriceV2 ? (
          <p className={`product-card__badge sale`}>Sale</p>
        ) : badgeText ? (
          <p className={`product-card__badge ${badgeColor} ${badgeText}`}>
            {badgeText}
          </p>
        ) : null}
        <div className="product-card__image-wrapper">
          {gallery ? (
            <Fragment>
              <swiper-container
                className="swiper"
                {...swiperParams}
                ref={swiper}
              >
                {[initialImages, ...productImages]
                  .filter(
                    (image, index, array) =>
                      array.findIndex(
                        (otherImage) => otherImage?.src === image?.src,
                      ) === index,
                  )
                  .map((item, index) => {
                    return (
                      <swiper-slide>
                        <a
                          href={productUrl}
                          aria-label={productTitle}
                          className={`product-card__image ${
                            variantImages.length > 1 ? "primary" : ""
                          }`}
                          tabIndex="-1"
                        >
                          <img
                            src={getSizedImageUrl(item?.src, "grande")}
                            alt={`${productTitle} - 1`}
                            className={`image image--product ${getImagePadding(
                              item,
                            )}`}
                            loading="lazy"
                          />
                        </a>
                      </swiper-slide>
                    );
                  })}
              </swiper-container>
              <div class="product-card__navigation">
                <button
                  type="button"
                  aria-label="Previous slide"
                  onClick={() => swiper?.current?.swiper.slidePrev(300)}
                >
                  <ArrowLeft />
                </button>
                <button
                  type="button"
                  aria-label="Next slide"
                  onClick={() => swiper?.current?.swiper.slideNext(300)}
                >
                  <ArrowRight />
                </button>
              </div>
            </Fragment>
          ) : (
            <a
              href={productUrl}
              aria-label={productTitle}
              className={`product-card__image ${
                variantImages.length > 1 ? "primary" : ""
              }`}
              tabIndex="-1"
            >
              <img
                src={getSizedImageUrl(initialImages?.src, "grande")}
                alt={`${productTitle} - 1`}
                className={`image image--product ${getImagePadding(
                  initialImages,
                )}`}
                loading="lazy"
              />
            </a>
          )}
        </div>
        {/* Product Card Quick Add for Non Bundle Items*/}
        {window.Shopify.country !== "CA" &&
          !product.bundleProducts &&
          product.availableForSale && (
            <div
              className={`product-add ${addTray ? "active" : ""}`}
              ref={quickAddContainerRef}
            >
              <button
                type="button"
                className="product-add__trigger"
                onClick={(e) =>
                  variants.length === 1 && !productGroupOptions
                    ? handleQuickAdd(e)
                    : setAddTray(!addTray)
                }
                ref={quickAddRef}
                aria-label="Add to cart"
              >
                <div className="product-add__icon">
                  <Plus />
                </div>
              </button>
              {addTray && (
                <div
                  className={`product-add__container ${
                    options.length == 0 && !productGroupOptions
                      ? "product-add--single"
                      : ""
                  }`}
                >
                  <button
                    type="button"
                    className="product-add__close"
                    onClick={() => closeTray(true)}
                    aria-label="Close"
                  >
                    <CloseIcon />
                  </button>
                  {options?.map((option, index) =>
                    option.name.toLowerCase() == groupStyle &&
                    productGroupOptions ? (
                      <div className="product-add__option">
                        <p>Please select your {groupStyle}</p>
                        {productGroupOptions?.map((item, i) => (
                          <button
                            type="button"
                            className={`option option${handleVariantActive(
                              item?.name,
                              item.value,
                            )}`}
                            onClick={() => {
                              handleVariantChange(item?.name, item.value);
                            }}
                          >
                            {item.value}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="product-add__option">
                        <p>Please select your {option?.name}</p>
                        {option.values?.map((item, i) => (
                          <button
                            type="button"
                            className={`option option${handleVariantActive(
                              option?.name,
                              item,
                            )}`}
                            onClick={() => {
                              handleVariantChange(option?.name, item);
                            }}
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    ),
                  )}
                  <button
                    className={`button button--black ${
                      addingToCart ? "button--disabled" : ""
                    }`}
                    onClick={(e) => handleQuickAdd(e)}
                    disabled={variant.availableForSale === false}
                  >
                    {variant.availableForSale === false ? (
                      <span>Out of Stock</span>
                    ) : (
                      <>
                        {"Add to Cart -"}
                        <ProductCardPrice product={product} variant={variant} />
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
      </div>
      {/* Product Card Content */}
      <div className="product-card__content">
        <a href={productUrl} className="product-card__url">
          <p className="product-card__title">{productTitle}</p>
        </a>
        <div className="product-card__footer">
          <p className="product-card__price">
            <ProductCardPrice product={product} variant={variant} />
          </p>

          {/* Product Card Swatches */}
          <div className="product-card__swatches">
            {colors?.length &&
              colors?.map(
                (c, i) =>
                  i < 4 && (
                    <button
                      aria-label={c}
                      type="button"
                      onClick={() => {
                        handleVariantChange(colorOptionName, c);
                      }}
                      className={`product-card__swatch product-card__swatch${handleVariantActive(
                        colorOptionName,
                        c,
                      )}`}
                    >
                      {/* <img
                        src={`${window.cdn.fileUrl}/${data?.handle}-${c
                          .toLowerCase()
                          .replace(/\(|\)/g, "")
                          .replace(/\s\//g, "")
                          .replace(/\s/g, "-")}_40x.png`}
                        alt={c}
                      /> */}
                      <SwatchImage
                        product={product}
                        handle={product?.handle}
                        optionValue={c}
                        className="product-card__swatch-inner"
                      />
                    </button>
                  ),
              )}
            {colors?.length > 4 && (
              <a className="product-card__swatch--more" href={productUrl}>
                +{colors?.length - 4}{" "}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
